/**
## Draggable List Row

This is a contextual component yielded by `sq-draggable-list`, and is not meant
to be used on its own.

### Parameters
@param {Action} staticClick - Optional - An action to trigger when the user's pointer (via touch or mouse click) is pressed and unpressed and has not moved enough to start dragging.

Sass dependencies at: `app/assets/stylesheets/dashboard/components/_sq-draggable-list.sass`
*/

import $ from 'jquery';
import { run, schedule } from '@ember/runloop';
import Component from '@ember/component';
import Draggabilly from 'draggabilly';
const EVENT_NAME_MAPPINGS = {
  dragStart: 'draggabillyDragStart',
  dragMove: 'draggabillyDragMove',
  dragEnd: 'draggabillyDragEnd',
  staticClick: 'draggabillyStaticClick'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['draggable-list__row'],
  draggabillyInstance: null,
  dragOnlyOnHandle: false,
  setCSSLeftOnDraggableRow: true,
  didInsertElement(...args) {
    this._super(...args);

    // Draggabilly setup can take a significant amount of time, resulting in jank (e.g. for `dialog-sheet` sliding into view)
    // Deferring this work unblocks the main thread so frames aren't dropped.
    schedule('afterRender', this, this.setupDraggabilly);
  },
  setupDraggabilly() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    const draggabilly = new Draggabilly(this.element, {
      axis: 'y',
      containment: true,
      handle: this.get('dragOnlyOnHandle') ? '.draggable-list__handle' : undefined
    });

    // adapted from Dashboard's `utils/draggabilly`
    for (const eventName of Object.keys(EVENT_NAME_MAPPINGS)) {
      draggabilly.on(eventName, (...args) => run(this, EVENT_NAME_MAPPINGS[eventName], draggabilly, ...args));
    }
    this.set('draggabillyInstance', draggabilly);
  },
  willDestroyElement(...args) {
    this._super(...args);
    const draggabillyInstance = this.get('draggabillyInstance');
    if (draggabillyInstance) {
      draggabillyInstance.destroy();
    }
  },
  draggabillyStaticClick() {
    if (this.staticClick) {
      this.staticClick();
    }
  },
  draggabillyDragStart() {
    const $el = $(this.element); // eslint-disable-line ember/no-jquery
    const $tableCells = $el.find('td');

    // Ensure element has width before we remove it from the document flow via
    // `position: fixed` below. Table rows require a different approach than
    // divs here.
    if ($tableCells.length > 0) {
      $tableCells.each(function (i, cell) {
        const $cell = $(cell); // eslint-disable-line ember/no-jquery
        $cell.css({
          'max-width': $cell.outerWidth(),
          width: $cell.outerWidth()
        });
      });
    } else {
      $el.css({
        width: $el.outerWidth()
      });
    }
    const css = {
      // prevent document.elementFromPoint from always finding the dragged item
      pointerEvents: 'none',
      // Remove from document flow to avoid shifting rows down, and fix start
      // position for draggabilly to transition based on
      position: 'fixed',
      top: this.element.getBoundingClientRect().top
    };
    if (this.setCSSLeftOnDraggableRow) {
      css.left = this.element.getBoundingClientRect().left;
    }
    $el.css(css);
    if (this.dragStart) {
      this.dragStart(this.element);
    }
  },
  draggabillyDragMove(draggabilly, event, pointer) {
    if (this.dragMove) {
      this.dragMove(draggabilly, event, pointer);
    }
  },
  draggabillyDragEnd() {
    // eslint-disable-next-line ember/no-jquery
    $(this.element).css({
      pointerEvents: '',
      // draggabilly sets an inline `position: relative` style on all rows, so
      // we're just restoring that here.
      position: 'relative',
      top: '',
      left: '',
      width: ''
    });
    // eslint-disable-next-line ember/no-jquery
    $(this.element).find('td').css({
      'max-width': '',
      width: ''
    });
    if (this.dragEnd) {
      this.dragEnd(this.element, this.get('model'));
    }
  }
});